import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MessageCircle, Send, Loader2 } from 'lucide-react';
import { BRAND } from '@/lib/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_type: 'Wedding Films',
    message: '',
};

export const Contact = () => {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error('Please fill in name, email, and message.');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Got it. We'll be in touch within 24 hours.");
            setForm(initial);
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not send. Try WhatsApp instead.';
            toast.error(typeof msg === 'string' ? msg : 'Could not send. Try WhatsApp instead.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-ink-soft py-24 md:py-32"
        >
            <div className="px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                <div className="lg:col-span-5">
                    <p className="text-xs uppercase tracking-[0.4em] text-gold mb-6">
                        Casting Now · 2026
                    </p>
                    <h2 className="font-serif text-bone text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light leading-[1.05]">
                        Ready to{' '}
                        <span className="italic text-bone-dim">cast your love story?</span>
                    </h2>
                    <p className="mt-6 text-bone-dim text-base lg:text-lg font-light leading-relaxed max-w-md">
                        Tell us about your day. We respond within 24 hours with a treatment
                        deck and availability.
                    </p>

                    <div className="mt-12 space-y-5">
                        <a
                            href={BRAND.whatsapp}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-whatsapp"
                            className="group flex items-center gap-4 text-bone hover:text-gold transition-colors duration-300"
                        >
                            <span className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-gold transition-colors">
                                <MessageCircle size={18} />
                            </span>
                            <span>
                                <span className="block text-[10px] uppercase tracking-[0.3em] text-bone-dim">
                                    WhatsApp
                                </span>
                                <span className="block font-serif text-xl tracking-tight">
                                    {BRAND.phone}
                                </span>
                            </span>
                        </a>
                        <a
                            href={`tel:+${BRAND.phoneRaw}`}
                            data-testid="contact-phone"
                            className="group flex items-center gap-4 text-bone hover:text-gold transition-colors duration-300"
                        >
                            <span className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-gold transition-colors">
                                <Phone size={18} />
                            </span>
                            <span>
                                <span className="block text-[10px] uppercase tracking-[0.3em] text-bone-dim">
                                    Call
                                </span>
                                <span className="block font-serif text-xl tracking-tight">
                                    {BRAND.phone}
                                </span>
                            </span>
                        </a>
                        <a
                            href={`mailto:${BRAND.email}`}
                            data-testid="contact-email"
                            className="group flex items-center gap-4 text-bone hover:text-gold transition-colors duration-300"
                        >
                            <span className="w-11 h-11 border border-white/15 flex items-center justify-center group-hover:border-gold transition-colors">
                                <Mail size={18} />
                            </span>
                            <span>
                                <span className="block text-[10px] uppercase tracking-[0.3em] text-bone-dim">
                                    Email
                                </span>
                                <span className="block font-serif text-xl tracking-tight break-all">
                                    {BRAND.email}
                                </span>
                            </span>
                        </a>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="contact-form"
                    className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7 self-start"
                >
                    <Field
                        label="Your name"
                        testid="contact-input-name"
                        value={form.name}
                        onChange={update('name')}
                        required
                    />
                    <Field
                        label="Email"
                        type="email"
                        testid="contact-input-email"
                        value={form.email}
                        onChange={update('email')}
                        required
                    />
                    <Field
                        label="Phone (optional)"
                        testid="contact-input-phone"
                        value={form.phone}
                        onChange={update('phone')}
                    />
                    <Field
                        label="Event date"
                        type="date"
                        testid="contact-input-date"
                        value={form.event_date}
                        onChange={update('event_date')}
                    />
                    <div className="md:col-span-2">
                        <label className="block text-[10px] uppercase tracking-[0.3em] text-bone-dim mb-3">
                            Project type
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {['Wedding Films', 'Wedding Photography', 'Commercial', 'Other'].map(
                                (t) => (
                                    <button
                                        type="button"
                                        key={t}
                                        data-testid={`contact-type-${t.toLowerCase().replace(/\s+/g, '-')}`}
                                        onClick={() => setForm((f) => ({ ...f, event_type: t }))}
                                        className={`text-[11px] uppercase tracking-[0.25em] px-4 py-2.5 border transition-all duration-300 ${
                                            form.event_type === t
                                                ? 'bg-bone text-ink border-bone'
                                                : 'text-bone-dim border-white/15 hover:text-bone hover:border-white/40'
                                        }`}
                                    >
                                        {t}
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-[10px] uppercase tracking-[0.3em] text-bone-dim mb-3">
                            Tell us about your day
                        </label>
                        <textarea
                            data-testid="contact-input-message"
                            value={form.message}
                            onChange={update('message')}
                            required
                            rows={5}
                            className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-bone font-light placeholder:text-bone-dim/40 transition-colors duration-300 resize-none"
                            placeholder="Where, when, and the story so far…"
                        />
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between pt-4">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-bone-dim">
                            We reply within 24 hrs
                        </p>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit-button"
                            className="group inline-flex items-center gap-3 bg-bone text-ink px-7 py-4 text-xs uppercase tracking-[0.25em] hover:bg-gold transition-colors duration-300 disabled:opacity-60"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" /> Sending
                                </>
                            ) : (
                                <>
                                    Send the brief
                                    <Send size={14} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
};

const Field = ({ label, testid, type = 'text', value, onChange, required }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-[0.3em] text-bone-dim mb-3">
            {label}
        </label>
        <input
            type={type}
            required={required}
            value={value}
            onChange={onChange}
            data-testid={testid}
            className="w-full bg-transparent border-b border-white/15 focus:border-gold outline-none py-3 text-bone font-light placeholder:text-bone-dim/40 transition-colors duration-300"
        />
    </div>
);
